import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import type {
  Fare,
  FareDetailsV2,
  FareSliceDeparture,
  FareSliceOutbound,
  TripDetailsV2,
  TripSlice,
} from "@b2bportal/air-shopping-api";
import type {
  AlgomerchTag,
  AppDispatch,
  FlightFareSelectionFunction,
  IFareData,
  IFareTrip,
  IFlightFares,
  IFlightListData,
} from "@b2bportal/core-types";
import { getCheapestFilteredFare } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { removeTimezone } from "@hopper-b2b/utilities";
import type { TripCategory } from "@overrides/types";
import type { FlightCardSummaryComponentProps } from "@tenant/flights";
import dayjs from "dayjs";
import { FlightSearchSelectors } from "../../../../search/store";
import { getSelectedFareShelfRatings } from "../../../../search/store/selectors";
import { fetchTripDetails } from "../../../store";
import {
  getFareData,
  getFlightData,
  getTripDetails,
} from "../../../store/selectors";

const DEFAULT_TAGS_LIMIT = 2;

export interface UseFlightCardParams {
  flight: IFlightListData<Fare | FareSliceOutbound | FareSliceDeparture>;
  isSelected: boolean;
  sliceIndex: number;
  onClick?: () => void;
}

export type UseFlightCardContext = {
  isFlightDetailsLoading: boolean;
  open: boolean;
  flightCardSummary?: FlightCardSummaryComponentProps;
  fareData: IFlightFares;
  tripCategory: TripCategory;
  fareShelfFilter: Array<number>;
  flightDetails?: TripDetailsV2;
  flightSlice?: TripSlice;
  fareShelfFilterWithUpsell: Array<number>;
  openFlightDetailsDialog: boolean;
  hasSelfTransferLayover: boolean;
  flightFareTrips: IFareTrip[];
};

export interface UseFlightCardResponse {
  context: UseFlightCardContext;
  handlers: {
    handleFlightCardClick: () => void;
    setOpenFlightDetailsDialog: Dispatch<SetStateAction<boolean>>;
    handleFareGridSelection: (fare: IFareData) => void;
  };
}

export const useFlightCard = ({
  flight,
  isSelected,
  sliceIndex = 0,
  onClick,
}: UseFlightCardParams): UseFlightCardResponse => {
  const dispatch = useDispatch<AppDispatch>();
  const { t, formatFiatCurrency, formatDateTime, DateTimeFormatStyle } =
    useI18nContext();

  const { sliceId, fares } = flight;

  // INTERNAL STATE
  const [fareGridFareShelfClick, setFareGridFareShelfClick] =
    useState<number>(0);
  const [openFlightDetailsDialog, setOpenFlightDetailsDialog] = useState(false);

  const flightFareTrips = useMemo(() => {
    const flightFares = flight.fares;
    return flightFares.map(
      (fare: Fare | FareSliceOutbound | FareSliceDeparture) => {
        return "example" in fare
          ? { fareId: fare.example.fare, tripId: fare.example.trip }
          : { fareId: fare.id, tripId: fare.tripId };
      }
    );
  }, [flight.fares]);

  // STATE
  const flightData = useSelector((state) => getFlightData(state, sliceId));
  const fareData = useSelector((state) => getFareData(state, fares));
  const fareShelfFilter = useSelector(getSelectedFareShelfRatings);

  const useFlightDetails = (flightFareTrips: IFareTrip[]) => {
    const selector = useMemo(
      () => (state) => getTripDetails(state, flightFareTrips),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [flightFareTrips.sort().toString()]
    );
    return useSelector(selector);
  };
  const { isLoading: isFlightDetailsLoading, data: flightDetails } =
    useFlightDetails(flightFareTrips);
  const tripCategory = useSelector(FlightSearchSelectors.getTripCategory);

  // CHILD PROPS
  const flightCardSummary = useMemo(() => {
    if (flightData == null) return undefined;

    const cheapestFiltedFare = getCheapestFilteredFare(
      fareData,
      fareShelfFilter
    );

    const getTags = (fares: IFlightFares) => {
      return Object.values(fares).reduce(
        (tags: AlgomerchTag[], fare: IFareData) => {
          return [...tags, ...(fare.tags ?? [])];
        },
        [] as AlgomerchTag[]
      );
    };

    const formatDuration = ({ days, hours, minutes }) => {
      const units = [
        { value: days, key: "days" },
        { value: hours, key: "hours" },
        { value: minutes, key: "minutes" },
      ];

      const durationString = units
        .filter((unit) => unit.value)
        .map((unit) => t(`flightDuration.${unit.key}`, { count: unit.value }))
        .join(" ");

      return durationString.trim();
    };

    const uniqueMarketingAirlines = new Set(
      flightData.segments.map((segment) => segment.marketingAirline)
    );
    const additionalAirlines =
      uniqueMarketingAirlines.size > 0
        ? uniqueMarketingAirlines.size - 1
        : undefined;

    return {
      duration: formatDuration(flightData.duration),
      currentPriceText: formatFiatCurrency(cheapestFiltedFare?.price.fiat),
      currentPrice: cheapestFiltedFare?.price,
      originCode: flightData.originCode,
      destinationCode: flightData.destinationCode,
      departureTime: formatDateTime(
        dayjs(removeTimezone(flightData.departureDateTime)).toDate(),
        DateTimeFormatStyle.ShortTime
      ),
      arrivalTime: formatDateTime(
        dayjs(removeTimezone(flightData.arrivalDateTime)).toDate(),
        DateTimeFormatStyle.ShortTime
      ),
      brandName: cheapestFiltedFare?.fareClass,
      fareName: cheapestFiltedFare?.fareName,
      primaryCarrier: flightData.airlineCode,
      airlineName: flightData.airlineName,
      tags: getTags(fareData),
      tagsLimit: DEFAULT_TAGS_LIMIT,
      flightStops: flightData.flightStops,
      additionalAirlines,
    };
  }, [
    flightData,
    fareData,
    fareShelfFilter,
    formatFiatCurrency,
    formatDateTime,
    DateTimeFormatStyle.ShortTime,
    t,
  ]);

  const fareShelfFilterWithUpsell = useMemo(() => {
    return fareShelfFilter.filter(
      (fareShelf) => fareShelf >= fareGridFareShelfClick
    );
  }, [fareShelfFilter, fareGridFareShelfClick]);

  const flightSlice = flightDetails?.slices[sliceIndex];

  // ON CLICKS
  const fetchFlightTripsDetails = useCallback(
    (fareTrips: IFareTrip[]) => {
      const uniqueTripIds: string[] = Array.from(
        new Set(fareTrips.map((fareTrip) => fareTrip.tripId))
      );
      uniqueTripIds.forEach((tripId: string) => {
        dispatch(fetchTripDetails({ tripId }));
      });
    },

    [dispatch]
  );

  const handleFlightCardClick = useCallback(() => {
    fetchFlightTripsDetails(flightFareTrips);
    onClick?.();
  }, [flightFareTrips, fetchFlightTripsDetails, onClick]);

  const handleFareGridSelection = useCallback(
    (fare: IFareData) => {
      // Set fare id or fare shelf and only upsell in fare details.
      setFareGridFareShelfClick(fare.fareShelf);
      handleFlightCardClick();
    },
    [handleFlightCardClick, setFareGridFareShelfClick]
  );

  return {
    context: {
      isFlightDetailsLoading,
      open: isSelected,
      flightCardSummary,
      fareData,
      tripCategory,
      fareShelfFilter,
      flightDetails,
      flightSlice,
      fareShelfFilterWithUpsell,
      openFlightDetailsDialog,
      flightFareTrips,
      hasSelfTransferLayover: flightData?.hasSelfTransferLayover ?? false,
    },
    handlers: {
      handleFlightCardClick,
      setOpenFlightDetailsDialog,
      handleFareGridSelection,
    },
  };
};
